import React from 'react';
import PropTypes from 'prop-types';

import Button from '$partials/ui/button';
import Image from '$partials/media/image';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */
/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const CardItem = ({
    body,
    cardTitle,
    ctaLabel,
    ctaLink,
    eyebrowIcon,
    eyebrowTitle,
    image = {},
    imageGreyOutline,
}) => {
    return (
        <div className='flex flex-col justify-between md:p-50 md:pt-[45px] p-30 pt-[26px] rounded-button bg-sand'>
            <div className='flex flex-col h-full'>
                <div className='flex flex-row items-center'>
                    {eyebrowIcon && (
                        <div
                            className='mr-10'
                            style={{
                                fontSize: '18px',
                                width: `${(eyebrowIcon?.width / eyebrowIcon?.height)}em` ,
                                maxWidth: '100%'
                            }}>
                            <Image
                                {...eyebrowIcon}
                                loading={''}/>
                        </div>
                    )}
                    <p className='type-style-7 text-orange'>
                        {eyebrowTitle}
                    </p>
                </div>
                {cardTitle && (
                    <p className='type-style-3 mt-18'>
                        {cardTitle}
                    </p>
                )}
                {body && (
                    <p className='type-style-5 mt-[7px] md:mt-10 text-light-grey'>
                        {body}
                    </p>
                )}
            </div>
            <div className='flex flex-col'>
                {ctaLabel && ctaLink && (
                    <div className='w-full flex mt-25 md:mt-20'>
                        <Button
                            as='a'
                            className='flex items-center self-start bg-orange text-white rounded-button type-style-7 px-20 py-13'
                            href={ctaLink}
                            type='button'>
                            {ctaLabel}
                        </Button>
                    </div>
                )}
                {image && (
                    <div className='mt-40 rounded-button overflow-hidden'>
                        {
                            image?.contentType === 'video/mp4' ? (
                                <video
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    src={image?.src}
                                    width={'100%'}/>
                            ) :  <Image
                                className={imageGreyOutline ? 'border border-grey' : ''}
                                loadWidth={797}
                                {...image}
                                style={{width: undefined}} />
                        }
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */

// Text Cards and Image Cards
const ModuleCards = ({
    cards,
    title,
    parent
}) => {
    const items = cards?.items;
    if (!items.length) {return null;}

    return (
        <div className={`bg-white relative ${parent === 'Home' ? 'py-40 md:py-50' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'}`}>
            <div className='layout-container'>

                <div className='bg-white'>
                    {title && (
                        <p className='px-10 sm:px-5 md:px-10 type-style-3 mb-20 md:mb-40'>
                            {title}
                        </p>
                    )}
                    <div className={`px-10 sm:px-5 md:px-10 grid sm:grid-cols-2 ${items.length <= 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-20 sm:gap-10 md:gap-20`}>
                        {items.map((item, i) => item ?
                            <CardItem
                                key={i}
                                {...item} />
                            : null
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* =========================================================================  */
/* Typing and export(s) */
/* =========================================================================  */

CardItem.propTypes = {
    body: PropTypes.string,
    cardTitle: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaLink: PropTypes.string,
    eyebrowTitle: PropTypes.string,
    eyebrowIcon: PropTypes.shape({
        src: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired
    }),
    image: PropTypes.shape({
        src: PropTypes.string.isRequired
    }),
    imageGreyOutline: PropTypes.bool,
};

ModuleCards.propTypes = {
    cards: PropTypes.object,
    title: PropTypes.string,
    parent: PropTypes.string
};

export default ModuleCards;
